import { StandardEvents } from '@shopify/events';
import { fetchConfig } from '@theme/utilities';

/*
 * GA4 client-id theme capture (PR-3 part 3b, 2026-08-14).
 *
 * conversions.ts's idempotency-ordering bug (fixed in pagewright#231) used to mark an order's GA4
 * send as "done" before confirming it actually succeeded — any order minted without a
 * `ga_client_id` note attribute could never be re-sent, ever. Theme orders never carried this
 * value: the tested extraction logic in pagewright's packages/web/src/ga-client-id.ts is only
 * consumed by a Lit SPA not embedded in this live theme.
 *
 * Captures unconditionally on the first cart-lines-update of the session — NOT gated on UTM
 * presence, deliberately: GA4 needs the client id on every order to be re-sendable, not just
 * paid-traffic ones. Listens on StandardEvents.cartLinesUpdate, the same theme-wide "cart
 * changed" broadcast cart-drawer.js/header-actions.js/gift-card-recipient-form.js already
 * consume, so this fires regardless of which add-to-cart path triggered it (product-form,
 * quick-add, pw-page-cart's single-page tier flow, etc).
 *
 * Review fix (2026-08-14): cartLinesUpdate is dispatched OPTIMISTICALLY — product-form.js fires
 * it before the /cart/add.js request that actually creates/mutates the cart even starts. Every
 * other document-level listener in this theme (cart-icon.js, cart-drawer.js,
 * component-cart-items.js) awaits event.promise before acting; this one didn't, so on a visitor's
 * very first add-to-cart the capture POST could race the cart-creating request itself — best case
 * losing the attribute, worst case landing the real line item in a discarded cart. Now waits for
 * event.promise (both branches) before capturing.
 *
 * Known gaps, not covered by this listener (see PR description): a returning visitor with an
 * already-persisted cart (Shopify carts persist ~14 days, sessionStorage does not) who checks out
 * without touching a cart line fires no cartLinesUpdate; a dynamic-checkout/Buy-it-now/Shop Pay
 * button bypasses the cart entirely, so cart attributes never get a chance to exist.
 */

/**
 * GA4's own cookie encodes the client id as `GA<version>.<two-part-id>.<extra segments>`
 * (e.g. `GA1.2.1892374651.1784654321`) — the id gtag/Measurement Protocol actually cares about is
 * the middle two dot-separated segments (`1892374651.1784654321`). Same extraction contract as
 * pagewright's packages/web/src/ga-client-id.ts (gaClientIdFromCookie) — kept in sync by hand
 * since this theme has no bundler to share that TypeScript module. (Note: product-form.js's own
 * pwGaClientId() is a third, independently-written copy of this same extraction — pre-existing,
 * out of scope for this PR, flagged as a drift risk worth a shared-contract test someday.)
 */
function gaClientIdFromCookie(cookieString) {
  var match = cookieString
    .split(';')
    .map(function (pair) {
      return pair.trim();
    })
    .find(function (pair) {
      return pair.indexOf('_ga=') === 0;
    });
  if (!match) return undefined;

  var value = match.slice('_ga='.length);
  var parts = value.split('.');
  if (parts.length < 4) return undefined;

  var idPart = parts[2];
  var tsPart = parts[3];
  if (!idPart || !tsPart || !/^\d+$/.test(idPart) || !/^\d+$/.test(tsPart)) return undefined;

  return idPart + '.' + tsPart;
}

var CAPTURED_KEY = 'pw_ga_client_id_captured';

/* sessionStorage throws SecurityError in blocked-storage contexts (Safari "block all cookies",
 * some embedded/ITP contexts) — guard both the read and the write so those visitors still get
 * captured (without the session guard) instead of the module throwing on every cart update. */
function alreadyCaptured() {
  try {
    return !!sessionStorage.getItem(CAPTURED_KEY);
  } catch (e) {
    return false;
  }
}
function markCaptured() {
  try {
    sessionStorage.setItem(CAPTURED_KEY, '1');
  } catch (e) {
    /* non-fatal — worst case we re-POST the same value next cart update */
  }
}

/* Own copy of the postCollectEvent beacon pattern (product-form.js:40, pw-image-search.js:67,
 * pw-search-collection-analytics.js:50) — this codebase's established convention is a small
 * per-file beacon helper, not a shared module. Fire-and-forget, never blocks capture. */
function postCollectEvent(payload) {
  try {
    var base = window.PW_BACKEND_URL || '';
    if (!base) return; // no beacon on a page with no backend configured, rather than posting to prod from staging
    var url = base + '/api/collect';
    var body = JSON.stringify(Object.assign({ event: 'ga_client_id_capture' }, payload));
    if (navigator.sendBeacon) {
      navigator.sendBeacon(url, new Blob([body], { type: 'application/json' }));
    } else {
      fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: body, keepalive: true });
    }
  } catch (e) {
    /* fire-and-forget, never blocks the UI */
  }
}

/*
 * Only marks the session-scoped guard on a CONFIRMED successful post — if the _ga cookie isn't
 * set yet (analytics blocked, GA still loading) or the request fails, nothing is marked, so the
 * next cart-lines-update in the same session retries rather than giving up permanently.
 */
function captureGaClientId() {
  if (alreadyCaptured()) return;

  var clientId = gaClientIdFromCookie(document.cookie);
  if (!clientId) return;

  fetch(
    Theme.routes.cart_update_url,
    fetchConfig('json', {
      body: JSON.stringify({ attributes: { ga_client_id: clientId } }),
    })
  )
    .then(function (response) {
      if (response.ok) {
        markCaptured();
        postCollectEvent({ ok: true });
      } else {
        console.warn('[ga-client-id] capture POST returned', response.status);
        postCollectEvent({ ok: false, status: response.status });
      }
    })
    .catch(function (err) {
      /* best-effort — retry on the next cart-lines-update */
      console.warn('[ga-client-id] capture failed', err);
      postCollectEvent({ ok: false });
    });
}

/* Review fix (2026-08-14): cartLinesUpdate fires BEFORE the cart mutation it describes completes
 * (product-form.js dispatches it, then starts the /cart/add.js fetch). Wait for event.promise —
 * matching cart-icon.js/cart-drawer.js/component-cart-items.js's own idiom — so this never races
 * the request that's still creating/mutating the cart. .catch too: a failed add-to-cart shouldn't
 * suppress capture, since the visitor's session/cookie state is still worth capturing. */
document.addEventListener(StandardEvents.cartLinesUpdate, function (event) {
  if (event && event.promise && typeof event.promise.then === 'function') {
    event.promise.then(captureGaClientId).catch(captureGaClientId);
  } else {
    captureGaClientId();
  }
});
